/**
 * useAuditEvents (Story 34.5 — Activity / Audit Log timeline)
 *
 * Cursor-paginated reader over GET /api/audit-events. House pattern: TanStack
 * `useInfiniteQuery` + `apiGet` + `{ data: { events, nextCursor } }` unwrap.
 *
 * The query key embeds the active filters, so any filter change starts a fresh
 * paginated query (pagination resets to the first page automatically — there is
 * no shared cursor between filter sets). `getNextPageParam` reads `nextCursor`
 * (null → undefined = no more pages).
 *
 * Events are read occurred_at DESC by the server; this hook only flattens the
 * page list and de-dupes by id (a row at a page boundary can appear twice if a
 * new event lands between fetches).
 */

import { useInfiniteQuery } from "@tanstack/react-query";
import { apiGet } from "../lib/apiClient";

/** One audit event as returned by GET /api/audit-events. */
export interface AuditEventDto {
  id: string;
  actorType: string;
  actorId: string | null;
  actorEmailSnapshot: string | null;
  action: string;
  resourceType: string;
  resourceId: string | null;
  resourceNameSnapshot: string | null;
  outcome: string;
  /** Epoch milliseconds. */
  occurredAt: number;
  requestId: string | null;
  cfRay: string | null;
  cfCountry: string | null;
  userAgent: string | null;
  /** Server-masked unless the caller is an Owner (VIEW_AUDIT_RAW_IPS). */
  ipAddress: string | null;
  metadata: Record<string, unknown> | null;
  diffBefore: Record<string, unknown> | null;
  diffAfter: Record<string, unknown> | null;
}

interface AuditEventsPage {
  events: AuditEventDto[];
  nextCursor: string | null;
}

/** Normalized, URL-derived filter set. Empty arrays / undefined = no filter. */
export interface AuditEventFilters {
  /** Free-text match on the actor email (server-side ilike). */
  actorId?: string;
  resourceType?: string[];
  action?: string[];
  /** Epoch ms (inclusive lower bound). */
  startTime?: number;
  /** Epoch ms (inclusive upper bound). */
  endTime?: number;
}

const PAGE_LIMIT = 50;

function buildQueryString(
  filters: AuditEventFilters,
  cursor: string | undefined
): string {
  const params = new URLSearchParams();
  params.set("limit", String(PAGE_LIMIT));
  if (cursor) params.set("cursor", cursor);
  if (filters.actorId) params.set("actorId", filters.actorId);
  // Multi-select filters are sent as repeated params; a single value covers the
  // common case and the server treats repeats as an OR set.
  for (const rt of filters.resourceType ?? []) params.append("resourceType", rt);
  for (const a of filters.action ?? []) params.append("action", a);
  if (filters.startTime != null) params.set("startTime", String(filters.startTime));
  if (filters.endTime != null) params.set("endTime", String(filters.endTime));
  return params.toString();
}

export function useAuditEvents(filters: AuditEventFilters) {
  return useInfiniteQuery<AuditEventsPage>({
    queryKey: ["audit-events", filters],
    initialPageParam: undefined,
    queryFn: async ({ pageParam }) => {
      const qs = buildQueryString(filters, pageParam as string | undefined);
      const res = await apiGet(`/api/audit-events?${qs}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(
          err.detail ?? err.title ?? "Failed to load activity events"
        );
      }
      const json = await res.json();
      return (json.data as AuditEventsPage) ?? { events: [], nextCursor: null };
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

/** Flatten the page list into a single de-duped (by id) event array. */
export function flattenAuditEvents(
  pages: AuditEventsPage[] | undefined
): AuditEventDto[] {
  if (!pages) return [];
  const seen = new Set<string>();
  const out: AuditEventDto[] = [];
  for (const page of pages) {
    for (const event of page.events) {
      if (seen.has(event.id)) continue;
      seen.add(event.id);
      out.push(event);
    }
  }
  return out;
}
